// src/DealsSection.js
import React from 'react';
import { BrowserProvider, Contract } from 'ethers';
import DCLPurchaseModal from './DCLPurchaseModal';
import { supabase } from './supabaseClient';

// MANA token contract on Polygon
const MANA_ADDRESS = '0xA1c57f48F0Deb89f569dFbE6E2B7f46D33606fD4';
const MANA_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)'
];

export default function DealsSection({
  dealsLoading,
  loading,
  showLocked,
  lockedDeals,
  availableDeals,
  walletAddress,
  selectedWalletType,
  ownedNFTs = [], // Add owned NFTs for token gating
  onError, // Add error handler prop
}) {
  // Helper function to get the correct Ethereum provider based on connected wallet type
  const getEthereumProvider = () => {
    if (selectedWalletType === 'magiceden') {
      return window.magicEden?.ethereum;
    }
    return window.ethereum;
  };
  const [dclQuantities, setDclQuantities] = React.useState({});
  const [showDCLModal, setShowDCLModal] = React.useState(false);
  const [selectedDCLDeal, setSelectedDCLDeal] = React.useState(null);
  const [selectedDCLQuantity, setSelectedDCLQuantity] = React.useState(1);

  // Helper function to log DCL purchase attempts
  const logDCLPurchaseAttempt = async (attemptData) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=log-purchase-attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(attemptData)
      });
    } catch (error) {
      console.error('Failed to log DCL purchase attempt:', error);
    }
  };

  // Check if user can purchase before showing modal
  const checkPurchaseEligibility = async (deal, quantity = 1) => {
    try {
      // First check if deal is available (expiration, supply pool, etc.)
      const { data: availabilityCheck, error: availError } = await supabase
        .rpc('is_deal_available', { p_deal_id: deal.id });
      
      if (availError) {
        console.error('Error checking deal availability:', availError);
        if (onError) {
          onError({
            title: 'Availability Check Failed',
            message: 'Unable to check deal availability. Please try again.',
            type: 'error'
          });
        }
        return false;
      }
      
      if (!availabilityCheck || availabilityCheck.length === 0 || !availabilityCheck[0].available) {
        const availability = availabilityCheck?.[0] || {};
        let errorTitle = 'Deal Not Available';
        let errorMessage = availability.reason || 'This deal is currently not available.';
        
        if (availability.is_expired) {
          errorTitle = 'Deal Expired';
          errorMessage = 'This deal has expired and is no longer available.';
        } else if (!availability.item_available) {
          errorTitle = 'Sold Out';
          errorMessage = `${deal.deal_name || 'This item'} is currently out of stock.`;
        }
        
        if (onError) {
          onError({
            title: errorTitle,
            message: errorMessage,
            type: 'warning'
          });
        }
        return false;
      }
      
      // Then check eligibility (token gates, wallet limits, etc.)
      const eligibilityResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=check-purchase-eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealId: deal.id,
          walletAddress: walletAddress,
          quantity: quantity,
          ownedNFTs: ownedNFTs // Include owned NFTs for token gating
        })
      });
      
      const eligibilityData = await eligibilityResponse.json();
      
      if (!eligibilityResponse.ok || !eligibilityData.canPurchase) {
        // Show error and stop purchase
        if (onError) {
          onError({
            title: 'Purchase Not Allowed',
            message: eligibilityData.error || 'You cannot purchase this item at this time.',
            type: 'error'
          });
        }
        return false;
      }
      
      return true;
    } catch (eligibilityError) {
      console.error('Error checking purchase eligibility:', eligibilityError);
      if (onError) {
        onError({
          title: 'Eligibility Check Failed',
          message: 'Unable to verify purchase eligibility. Please try again.',
          type: 'error'
        });
      }
      return false;
    }
  };

  // Handle DCL wearable purchase with MANA
  const handleDCLPurchase = async (deal, quantity = 1) => {
    let attemptId = null;
    let userAddress = null;
    
    // Calculate bundle price from deal_items or fallback to single item
    const dealItems = deal.deal_items && deal.deal_items.length > 0 ? deal.deal_items : 
      [{ dcl_price_mana: deal.dcl_price_mana }];
    
    // If RNG is enabled, only charge for one item
    const bundlePrice = deal.rng_enabled ? 
      parseFloat(dealItems[0].dcl_price_mana) : 
      dealItems.reduce((sum, item) => sum + (parseFloat(item.dcl_price_mana) || 0), 0);
    
    const totalPrice = bundlePrice * quantity;
    
    try {
      // Log the initial attempt
      const initialAttempt = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=log-purchase-attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet: walletAddress,
          deal_id: deal.id,
          deal_name: deal.deal_name,
          dcl_collection_address: deal.dcl_collection_address,
          dcl_item_id: deal.dcl_item_id,
          quantity: quantity,
          price_mana: bundlePrice, // Use bundle price for logging
          status: 'initiated',
          user_agent: navigator.userAgent
        })
      });
      
      if (initialAttempt.ok) {
        const { attempt } = await initialAttempt.json();
        attemptId = attempt?.id;
      }
      
      // Get the correct provider based on wallet type
      const ethereumProvider = getEthereumProvider();
      
      // For all wallets including Magic Eden, switch to Polygon network
      // (Magic Eden EVM provider supports wallet_switchEthereumChain per their sandbox)
      await ethereumProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }], // Polygon mainnet
      });

      // For Magic Eden, get address from multichain but use ethereum provider for transactions
      let provider, signer;
      if (selectedWalletType === 'magiceden') {
        const multiChain = window.magicEden?.multiChain;
        if (multiChain && multiChain.isConnected) {
          // Magic Eden multiChain detected - getting EVM address
          // Get ethereum address from multichain (works for Polygon)
          const multichainResponse = multiChain.currentMultiChainResponse;
          if (multichainResponse?.addresses?.ethereum) {
            // Magic Eden returns addresses as an array, take the first one
            const ethAddresses = multichainResponse.addresses.ethereum;
            userAddress = Array.isArray(ethAddresses) ? ethAddresses[0] : ethAddresses;
            // Use ethereum provider for actual transactions (Magic Eden pattern)
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
          } else {
            // Magic Eden multiChain missing EVM address, falling back to ethereum provider
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
            userAddress = await signer.getAddress();
          }
        } else {
          console.log('⚠️ DEALS: Magic Eden multiChain not available, using ethereum provider');
          provider = new BrowserProvider(ethereumProvider);
          signer = await provider.getSigner();
          userAddress = await signer.getAddress();
        }
      } else {
        // For MetaMask and Coinbase, use standard ethereum provider
        provider = new BrowserProvider(ethereumProvider);
        signer = await provider.getSigner();
        userAddress = await signer.getAddress();
      }

      // 3. Check if user has enough MANA
      const manaContract = new Contract(MANA_ADDRESS, MANA_ABI, signer);
      const balance = await manaContract.balanceOf(userAddress);
      const decimals = await manaContract.decimals();
      // Use Math.round instead of Math.floor for more accurate decimal conversion
      const requiredAmount = BigInt(Math.round(totalPrice * Math.pow(10, Number(decimals))));

      if (balance < requiredAmount) {
        const bundleDescription = dealItems.length > 1 
          ? `${bundlePrice} bundle × ${quantity}` 
          : `${bundlePrice} × ${quantity}`;
        const errorMessage = `You need ${totalPrice} MANA (${bundleDescription}) but only have ${Number(balance) / Math.pow(10, Number(decimals))} MANA.`;
        
        if (onError) {
          onError({
            title: 'Insufficient MANA Balance',
            message: errorMessage,
            type: 'warning'
          });
        }
        
        // Throw error to prevent success modal from showing
        throw new Error(`Insufficient MANA: ${errorMessage}`);
      }

      // 4. Send MANA payment to beneficiary
      const paymentTx = await manaContract.transfer(
        deal.dcl_beneficiary_address,
        requiredAmount,
        { gasLimit: 300_000, chainId: 137 } // Polygon mainnet
      );
      
      // Update attempt status to payment_sent
      if (attemptId) {
        await logDCLPurchaseAttempt({
          id: attemptId,
          status: 'payment_sent',
          payment_tx_hash: paymentTx.hash,
          payment_sent_at: new Date().toISOString()
        });
      }

      // 5. Wait for payment confirmation
      const receipt = await paymentTx.wait();
      
      // Update attempt status to payment_confirmed
      if (attemptId) {
        await logDCLPurchaseAttempt({
          id: attemptId,
          status: 'payment_confirmed',
          payment_confirmed_at: new Date().toISOString()
        });
      }

      // 6. Call backend to mint the DCL wearable (include owned NFTs for token gating)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=purchase-dcl-wearable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dealId: deal.id,
          walletAddress: userAddress,
          paymentTxHash: paymentTx.hash,
          quantity: quantity,
          ownedNFTs: ownedNFTs // Include owned NFTs for token gating security
        })
      });

      const result = await response.json();

      if (result.success) {
        // Update attempt status to completed
        if (attemptId) {
          await logDCLPurchaseAttempt({
            id: attemptId,
            status: 'completed',
            mint_tx_hash: result.mintTxHash,
            mint_completed_at: new Date().toISOString()
          });
        }
        
        // Return success with transaction hashes
        return {
          success: true,
          paymentTxHash: paymentTx.hash,
          mintTxHash: result.mintTxHash,
          mintTxHashes: result.mintTxHashes || [result.mintTxHash], // Include array of all mint transactions
          minted_item: result.minted_item, // Make sure we pass through the minted_item
          random_value: result.random_value, // Include random value for RNG deals
          rng_pool: result.rng_pool // Include RNG pool for chart display
        };
      } else {
        throw new Error(result.error || 'Purchase failed');
      }

    } catch (error) {
      console.error('DCL purchase failed:', error);
      
      // Log the failure - if we don't have an attemptId, create a new attempt record for the failure
      if (attemptId) {
        // Update existing attempt
        await logDCLPurchaseAttempt({
          id: attemptId,
          status: (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || error.message?.includes('user denied') || error.message?.includes('User denied') || error.message?.includes('User rejected the request')) ? 'cancelled' : 'failed',
          error_message: error.message,
          error_stage: 'client_side'
        });
      } else {
        // Create new failure record if initial attempt wasn't logged
        await logDCLPurchaseAttempt({
          wallet: userAddress || walletAddress,
          deal_id: deal.id,
          deal_name: deal.deal_name,
          dcl_collection_address: deal.dcl_collection_address,
          dcl_item_id: deal.dcl_item_id,
          quantity: quantity,
          price_mana: bundlePrice, // Use bundle price for logging
          status: (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || error.message?.includes('user denied') || error.message?.includes('User denied') || error.message?.includes('User rejected the request')) ? 'cancelled' : 'failed',
          error_message: error.message,
          error_stage: 'client_side',
          user_agent: navigator.userAgent
        });
      }
      
      if (onError) {
        // Extract the actual error message from nested error objects
        let actualErrorMessage = error.message || '';
        
        // Check for nested error messages (common with Ledger and other hardware wallets)
        if (error.data?.cause?.message) {
          actualErrorMessage = error.data.cause.message;
        } else if (error.data?.message) {
          actualErrorMessage = error.data.message;
        } else if (error.cause?.message) {
          actualErrorMessage = error.cause.message;
        } else if (error.error?.message) {
          actualErrorMessage = error.error.message;
        }
        
        // If we still have the full error object as a string, try to extract just the message
        if (actualErrorMessage.includes('could not coalesce error') && actualErrorMessage.includes('"message":')) {
          try {
            const match = actualErrorMessage.match(/"message":\s*"([^"]+)"/);
            if (match && match[1]) {
              actualErrorMessage = match[1];
            }
          } catch (e) {
            // If parsing fails, keep the original message
          }
        }

        if (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || actualErrorMessage?.includes('user denied') || actualErrorMessage?.includes('User denied') || actualErrorMessage?.includes('User rejected the request') || actualErrorMessage?.includes('User rejected the transaction') || actualErrorMessage?.includes('Ledger: User rejected')) {
          onError({
            title: 'Transaction Cancelled',
            message: 'You cancelled the transaction. No charges were made to your wallet.',
            type: 'info'
          });
        } else if (error.code === 'INSUFFICIENT_FUNDS') {
          onError({
            title: 'Insufficient Funds',
            message: 'You don\'t have enough MATIC for gas fees. Please add more MATIC to your wallet.',
            type: 'warning'
          });
        } else if (actualErrorMessage?.includes('network')) {
          onError({
            title: 'Network Error',
            message: 'There was a problem connecting to the blockchain. Please check your internet connection and try again.',
            type: 'warning'
          });
        } else if (actualErrorMessage?.includes('Ledger:') || actualErrorMessage?.includes('Ethereum app closed') || actualErrorMessage?.includes('Device is locked')) {
          onError({
            title: 'Ledger Wallet Issue',
            message: 'Your Ledger device needs attention. Please open the Ethereum app on your Ledger device and try again.',
            type: 'warning'
          });
        } else {
          onError({
            title: 'Purchase Failed',
            message: actualErrorMessage || 'An unexpected error occurred during the purchase.',
            type: 'error'
          });
        }
      }
      
      // Re-throw the error to prevent success modal from showing
      throw error;
    }
  };

  // Fetch a short-lived JWT and then open the Shopify link
  const handleShopNow = async (deal) => {
    try {
      // First, get the deal access details
      const accessUrl = new URL(
        `${process.env.REACT_APP_API_URL}/api/deals`
      );
      accessUrl.searchParams.set('action', 'verify-access');
      accessUrl.searchParams.set('dealId', deal.id);
      accessUrl.searchParams.set('walletAddress', walletAddress);

      const accessRes = await fetch(accessUrl.toString());
      if (!accessRes.ok) throw new Error(`Access verification failed: ${accessRes.status}`);
      const accessData = await accessRes.json();

      // Determine the target URL - prioritize exclusive product URL, fallback to discount link
      let targetUrl = accessData.exclusiveProductUrl || accessData.discountLink;
      
      if (!targetUrl) {
        throw new Error('No product URL available for this deal');
      }

      // If we have an exclusive product URL, get a JWT token for it
      if (accessData.exclusiveProductUrl) {
        const tokenUrl = new URL(
          `${process.env.REACT_APP_API_URL}/api/deals`
        );
        tokenUrl.searchParams.set('action', 'token');
        tokenUrl.searchParams.set('dealName', deal.deal_name);
        tokenUrl.searchParams.set('wallet', walletAddress);

        const tokenRes = await fetch(tokenUrl.toString());
        if (!tokenRes.ok) throw new Error(`Token generation failed: ${tokenRes.status}`);
        const { accessToken } = await tokenRes.json();

        // Add access token to URL
        const separator = targetUrl.includes('?') ? '&' : '?';
        targetUrl = `${targetUrl}${separator}access_token=${encodeURIComponent(accessToken)}`;
      }

      // Open the target URL
      window.open(targetUrl, '_blank', 'noopener noreferrer');
      
    } catch (err) {
      console.error('Failed to access deal:', err);
      alert('Failed to access this deal. Please try again later.');
    }
  };

  // Decide which list to show
  const deals = showLocked ? lockedDeals : availableDeals;

  // Loading / empty states
  if (dealsLoading || loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'white' }}>
        <div style={{ 
          fontSize: '1.2rem', 
          fontWeight: '600',
          background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
          backgroundSize: '400% 400%',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          animation: 'rainbowFlow 3s ease-in-out infinite'
        }}>
          Loading deals...
        </div>
      </div>
    );
  }
  if (!Array.isArray(deals) || deals.length === 0) {
    return (
      <p style={{ textAlign: 'center' }}>
        {showLocked
          ? 'No locked deals—you qualify for everything!'
          : 'No deals available for your NFTs.'}
      </p>
    );
  }

  return (
    <div className="tile-container" style={{ justifyContent: 'center' }}>
      {deals.map((deal) => {
        
        const reqs =
          Array.isArray(deal.required_items) && deal.required_items.length > 0
            ? deal.required_items.map(req => ({
                ...req,
                // Auto-generate URL if empty and we have collection data
                url: req.url || 
                     (req.collection && req.item_id 
                       ? `https://decentraland.org/marketplace/contracts/${req.collection}/items/${req.item_id}`
                       : req.collection
                         ? `https://decentraland.org/marketplace/browse?assetType=item&vendor=decentraland&contracts=${req.collection}`
                         : null)
              }))
            : [{ 
                collection: deal.collection, 
                item_id: deal.required_item_id,
                name: deal.required_item_name,
                url: deal.required_item_url || 
                     (deal.collection && deal.required_item_id 
                       ? `https://decentraland.org/marketplace/contracts/${deal.collection}/items/${deal.required_item_id}`
                       : null)
              }].filter(req => req.collection); // Only include requirements that have a collection

        return (
          <div
            key={deal.id}
            className="tile"
            style={{
              border: '1px solid #ddd',
              padding: '1rem',
              margin: '0.5rem',
              borderRadius: '0.5rem',
              maxWidth: '300px',
            }}
          >
            <h3 style={{ marginBottom: '0.5rem' }}>
              {deal.deal_name}
              <span style={{ 
                marginLeft: '0.5rem',
                padding: '0.2rem 0.5rem',
                borderRadius: '0.3rem',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                backgroundColor: deal.deal_type === 'dcl_wearable' ? '#8a2be2' : '#ff8c00',
                color: 'white'
              }}>
                {deal.deal_type === 'dcl_wearable' ? 'WEARABLE' : 'PHYGITAL'}
              </span>
            </h3>

            {/* Deal image(s) - show bundle as overlapping cards */}
            <div style={{ position: 'relative', minHeight: '200px', marginBottom: '0.5rem' }}>
              {(() => {
                // Show bundle images or fallback to single image
                const dealImages = [];
                
                if (deal.deal_items && deal.deal_items.length > 0) {
                  // Use bundle items
                  deal.deal_items.forEach((item, index) => {
                    if (item.deal_image) {
                      dealImages.push({ src: item.deal_image, index });
                    }
                  });
                } else if (deal.deal_image) {
                  // Fallback to single image
                  dealImages.push({ src: deal.deal_image, index: 0 });
                }
                
                if (dealImages.length === 0) return null;

                // For RNG deals, show a grid layout
                if (deal.rng_enabled) {
                  const rngItems = deal.deal_items || [];
                  const totalChance = 100;
                  const chancePerItem = totalChance / rngItems.length;
                  
                  if (rngItems.length >= 4) {
                    // 2x2 grid for 4+ items - needs more height
                    return (
                      <div style={{ 
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        height: '280px',
                        padding: '0.5rem'
                      }}>
                        {/* Top row */}
                        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', gap: '0.5rem' }}>
                          {rngItems.slice(0, 2).map((item, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              padding: '0.5rem',
                              backgroundColor: 'rgba(138, 43, 226, 0.1)',
                              borderRadius: '8px',
                              border: '1px solid rgba(138, 43, 226, 0.3)',
                              minWidth: '80px',
                              flex: 1,
                              height: '100%',
                              boxSizing: 'border-box'
                            }}>
                              {item.deal_image && (
                                <img
                                  src={item.deal_image}
                                  alt={`Reward ${index + 1}`}
                                  style={{
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '6px',
                                    objectFit: 'cover',
                                    marginBottom: '0.25rem',
                                    flexShrink: 0
                                  }}
                                />
                              )}
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#ccc', 
                                textAlign: 'center',
                                wordWrap: 'break-word',
                                overflow: 'hidden',
                                maxWidth: '100%'
                              }}>
                                🎲 {chancePerItem.toFixed(0)}%
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Middle OR row */}
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <div style={{ fontSize: '0.8rem', color: '#8a2be2', fontWeight: 'bold' }}>OR</div>
                        </div>
                        
                        {/* Bottom row */}
                        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', gap: '0.5rem' }}>
                          {rngItems.slice(2, 4).map((item, index) => (
                            <div key={index + 2} style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              padding: '0.5rem',
                              backgroundColor: 'rgba(138, 43, 226, 0.1)',
                              borderRadius: '8px',
                              border: '1px solid rgba(138, 43, 226, 0.3)',
                              minWidth: '80px',
                              flex: 1,
                              height: '100%',
                              boxSizing: 'border-box'
                            }}>
                              {item.deal_image && (
                                <img
                                  src={item.deal_image}
                                  alt={`Reward ${index + 3}`}
                                  style={{
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '6px',
                                    objectFit: 'cover',
                                    marginBottom: '0.25rem',
                                    flexShrink: 0
                                  }}
                                />
                              )}
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#ccc', 
                                textAlign: 'center',
                                wordWrap: 'break-word',
                                overflow: 'hidden',
                                maxWidth: '100%'
                              }}>
                                🎲 {chancePerItem.toFixed(0)}%
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  } else if (rngItems.length === 3) {
                    // 3 items in a row
                    return (
                      <div style={{ 
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        height: '220px',
                        padding: '0.5rem',
                        gap: '0.25rem'
                      }}>
                        {rngItems.map((item, index) => (
                          <React.Fragment key={index}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              padding: '0.25rem',
                              backgroundColor: 'rgba(138, 43, 226, 0.1)',
                              borderRadius: '6px',
                              border: '1px solid rgba(138, 43, 226, 0.3)',
                              minWidth: '60px'
                            }}>
                              {item.deal_image && (
                                <img
                                  src={item.deal_image}
                                  alt={`Reward ${index + 1}`}
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                    marginBottom: '0.25rem'
                                  }}
                                />
                              )}
                              <div style={{ fontSize: '0.6rem', color: '#ccc', textAlign: 'center' }}>
                                🎲 {chancePerItem.toFixed(0)}%
                              </div>
                            </div>
                            {index < rngItems.length - 1 && (
                              <div style={{ fontSize: '0.7rem', color: '#8a2be2', fontWeight: 'bold' }}>OR</div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    );
                  } else if (rngItems.length === 2) {
                    // 2 items side by side
                    return (
                      <div style={{ 
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        height: '200px',
                        padding: '0.5rem',
                        gap: '1rem'
                      }}>
                        {rngItems.map((item, index) => (
                          <React.Fragment key={index}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              padding: '0.75rem',
                              backgroundColor: 'rgba(138, 43, 226, 0.1)',
                              borderRadius: '10px',
                              border: '1px solid rgba(138, 43, 226, 0.3)',
                              minWidth: '100px',
                              flex: 1
                            }}>
                              {item.deal_image && (
                                <img
                                  src={item.deal_image}
                                  alt={`Reward ${index + 1}`}
                                  style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '6px',
                                    objectFit: 'cover',
                                    marginBottom: '0.5rem'
                                  }}
                                />
                              )}
                              <div style={{ fontSize: '0.8rem', color: '#ccc', textAlign: 'center' }}>
                                🎲 {chancePerItem.toFixed(0)}%
                              </div>
                            </div>
                            {index < rngItems.length - 1 && (
                              <div style={{ fontSize: '1rem', color: '#8a2be2', fontWeight: 'bold' }}>OR</div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    );
                  }
                }
                
                if (dealImages.length === 1) {
                  // Single image - use original layout
                  return (
                    <img
                      src={dealImages[0].src}
                      alt={deal.deal_name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        width: 'auto',
                        height: 'auto',
                        borderRadius: '0.25rem',
                      }}
                    />
                  );
                }
                
                // Multiple images - create overlapping card effect
                return (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    {dealImages.map((img, displayIndex) => (
                      <img
                        key={displayIndex}
                        src={img.src}
                        alt={`${deal.deal_name} - Item ${img.index + 1}`}
                        style={{
                          position: 'absolute',
                          maxWidth: '180px',
                          maxHeight: '180px',
                          width: 'auto',
                          height: 'auto',
                          borderRadius: '0.25rem',
                          border: '2px solid #444',
                          objectFit: 'cover',
                          // Overlapping card effect
                          left: `calc(50% + ${(displayIndex - (dealImages.length - 1) / 2) * 20}px)`,
                          top: `calc(50% + ${displayIndex * 5}px)`,
                          transform: `translate(-50%, -50%) rotate(${(displayIndex * 5) - ((dealImages.length - 1) * 2.5)}deg)`,
                          zIndex: dealImages.length - displayIndex,
                          boxShadow: displayIndex > 0 ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.2)',
                        }}
                      />
                    ))}
                    
                    {/* Bundle count indicator */}
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      backgroundColor: '#8a2be2',
                      color: 'white',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      border: '2px solid #1a1a1a',
                      zIndex: 1000,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}>
                      {dealImages.length}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Show NFT requirements if they exist */}
            {reqs[0]?.collection && (
              <p style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                <strong>Requires NFT from:</strong>
                <br />
                {reqs.map((req, i) => (
                  <span key={i}>
                    {req.url ? (
                      <a
                        className="marketplaceLink"
                        href={req.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {req.name || req.collection}
                      </a>
                    ) : (
                      req.name || req.collection
                    )}
                    {i < reqs.length - 1
                      ? deal.logic === 'AND'
                        ? ' and '
                        : ' or '
                      : ''}
                  </span>
                ))}
              </p>
            )}

            {/* DCL wearable description */}
            {deal.deal_type === 'dcl_wearable' && (
              <p style={{ wordWrap: 'break-word', whiteSpace: 'normal', fontSize: '0.9rem', opacity: 0.9 }}>
                <strong>Decentraland Wearable</strong>
                <br />
                {deal.rng_enabled 
                  ? 'Purchase with MANA to receive a random reward from the pool' 
                  : reqs[0]?.collection 
                    ? 'Purchase with MANA after owning required NFTs' 
                    : 'Purchase directly with MANA - no NFT requirements'
                }
              </p>
            )}

            {!showLocked && (
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  marginTop: '0.5rem',
                }}
              >
                {/* DCL Wearable deals */}
                {deal.deal_type === 'dcl_wearable' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '0.5rem', 
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(138, 43, 226, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(138, 43, 226, 0.3)'
                  }}>
                    {/* Price and limits info */}
                    <div style={{ color: '#fff', fontSize: '0.9rem', textAlign: 'center' }}>
                      {(() => {
                        // Calculate bundle info
                        const dealItems = deal.deal_items && deal.deal_items.length > 0 ? deal.deal_items : 
                          [{ dcl_price_mana: deal.dcl_price_mana }];
                        
                        // If RNG is enabled, only show the price of one item
                        if (deal.rng_enabled) {
                          const singleItemPrice = dealItems[0].dcl_price_mana;
                          return (
                            <>
                              <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                                {singleItemPrice} MANA
                              </div>
                              <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.25rem' }}>
                                Random reward from {dealItems.length} items
                              </div>
                            </>
                          );
                        }
                        
                        // For non-RNG deals, show bundle price
                        const bundlePrice = dealItems.reduce((sum, item) => sum + (parseFloat(item.dcl_price_mana) || 0), 0);
                        const itemCount = dealItems.length;
                        
                        return (
                          <>
                            <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                              {bundlePrice} MANA {itemCount > 1 ? `(${itemCount} items)` : ''}
                            </div>
                            {itemCount > 1 && (
                              <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.25rem' }}>
                                Bundle: {dealItems.map(item => `${item.dcl_price_mana} MANA`).join(' + ')}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Quantity selector for multi-mint */}
                    {deal.multi_mint_enabled && !deal.rng_enabled && (
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginTop: '0.5rem'
                      }}>
                        <button
                          className="no-btn"
                          onClick={() => {
                            const currentQty = dclQuantities[deal.id] || 1;
                            if (currentQty > 1) {
                              setDclQuantities(prev => ({
                                ...prev,
                                [deal.id]: currentQty - 1
                              }));
                            }
                          }}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.9rem',
                            backgroundColor: '#e74c3c'
                          }}
                        >
                          -
                        </button>
                        <span style={{ color: '#fff' }}>
                          {dclQuantities[deal.id] || 1}
                        </span>
                        <button
                          className="no-btn"
                          onClick={() => {
                            const currentQty = dclQuantities[deal.id] || 1;
                            setDclQuantities(prev => ({
                              ...prev,
                              [deal.id]: currentQty + 1
                            }));
                          }}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.9rem',
                            backgroundColor: '#2ecc71'
                          }}
                        >
                          +
                        </button>
                      </div>
                    )}

                    {/* Buy button */}
                    <button
                      className="rainbowBtn"
                      style={{
                        width: '100%',
                        textAlign: 'center',
                        fontSize: '0.9rem',
                        marginTop: '0.5rem'
                      }}
                      onClick={async () => {
                        const quantity = deal.rng_enabled ? 1 : (dclQuantities[deal.id] || 1);
                        const canPurchase = await checkPurchaseEligibility(deal, quantity);
                        if (canPurchase) {
                          setSelectedDCLDeal(deal);
                          setSelectedDCLQuantity(quantity);
                          setShowDCLModal(true);
                        }
                      }}
                    >
                      Buy Now
                    </button>
                  </div>
                )}

                {/* Regular Shopify deals */}
                {deal.deal_type !== 'dcl_wearable' && (
                  <>
                    {deal.has_discount_link && (
                  <button
                    className="rainbowBtn"
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      fontSize: '0.9rem',
                    }}
                        onClick={() => handleShopNow(deal)}
                      >
                        {deal.has_discount_code ? 'Apply Discount' : 'Get Coupon'}
                  </button>
                )}

                    {deal.has_exclusive_product_url && (
                  <button
                    className="rainbowBtn"
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      fontSize: '0.9rem',
                    }}
                    onClick={() => handleShopNow(deal)}
                  >
                    Shop Now
                  </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* DCL Purchase Modal */}
      {showDCLModal && selectedDCLDeal && (
        <DCLPurchaseModal
          deal={selectedDCLDeal}
          quantity={selectedDCLQuantity}
          onError={onError}
          onClose={() => {
            setShowDCLModal(false);
            setSelectedDCLDeal(null);
            setSelectedDCLQuantity(1);
          }}
          onConfirm={() => handleDCLPurchase(selectedDCLDeal, selectedDCLQuantity)}
          onDone={() => {
            setShowDCLModal(false);
            setSelectedDCLDeal(null);
            setSelectedDCLQuantity(1);
          }}
        />
      )}
    </div>
  );
}
